import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, useTheme } from "@mui/material";
import type {
	CellStyle,
	CellStyleFunc,
	ColDef,
	ICellRendererParams,
	ValueGetterParams,
} from "ag-grid-community";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import type { MouseEvent } from "react";

import type {
	InspectionWithValues,
	ProfileInspectionsResult,
} from "../../models/Inspection";
import type {
	AverageRow,
	RollingDimensionSummary,
} from "../../models/RollingSummary";
import {
	WEIGHT_ITEM_CODE,
	type SectionSpecItem,
} from "@/shared/models/SectionSpec";
import { getSummaryMeasurementStyle } from "../../utils/summaryCellStyle";
import { isInspectionEvaluated } from "../../utils/hasInspectionTolerances";
import { MeasurementValue } from "../../shared/components/MeasurementValue";
import {
	getSummaryItems,
	hasWeightColumn,
	weightApplies,
} from "../../utils/summaryItems";
import { formatMeasurement } from "@/shared/utils/measurementPrecision";

const ROLLING_GRID_COLUMN_WIDTHS = {
	date: 200,
	section: 120,
	heat: 80,
	inspector: 80,
	weight: 90,
	dimension: 90,
	actions: 90,
} as const;

const EMPTY_CELL_VALUE = "-";
const isAverage = (row: unknown): row is AverageRow =>
	typeof row === "object" &&
	row !== null &&
	"__average" in row &&
	row.__average === true;

const isInspection = (row: unknown): row is InspectionWithValues =>
	typeof row === "object" && row !== null && "ProfileInspectionId" in row;

const fmt = (value: number | null | undefined) =>
	value == null ? EMPTY_CELL_VALUE : formatMeasurement(value);

const displayValue = (value: unknown) =>
	value === null || value === undefined || value === ""
		? EMPTY_CELL_VALUE
		: value;

interface Props {
	inspections: InspectionWithValues[];
	applicableItemsBySpec?: ProfileInspectionsResult["applicableItemsBySpec"];
	/** Metadata from the selected section only enriches matching columns. */
	specItems: SectionSpecItem[];
	summaryDimensions: RollingDimensionSummary[];
	onEdit: (row: InspectionWithValues) => void;
	onDelete: (row: InspectionWithValues) => void;
}

interface InspectionActionsCellParams {
	onEdit: (row: InspectionWithValues) => void;
	onDelete: (row: InspectionWithValues) => void;
}

const InspectionActionsCell = (
	params: ICellRendererParams<InspectionWithValues | AverageRow> &
		InspectionActionsCellParams,
) => {
	if (!isInspection(params.data)) return null;
	const inspection = params.data;

	const handleEdit = (event: MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		params.onEdit(inspection);
	};
	const handleDelete = (event: MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		params.onDelete(inspection);
	};

	return (
		<Box
			className="inspection-actions"
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				height: "100%",
				gap: 0.5,
			}}
		>
			<IconButton
				aria-label="Edit inspection"
				size="small"
				onClick={handleEdit}
			>
				<EditIcon fontSize="small" color="primary" />
			</IconButton>
			<IconButton
				aria-label="Delete inspection"
				size="small"
				onClick={handleDelete}
			>
				<DeleteIcon fontSize="small" color="error" />
			</IconButton>
		</Box>
	);
};

/** Columns follow the revision with the most items among the displayed samples. */
export const useRollingTableData = ({
	inspections,
	applicableItemsBySpec,
	specItems,
	summaryDimensions,
	onEdit,
	onDelete,
}: Props) => {
	const theme = useTheme();
	const summaryItems = getSummaryItems({ inspections, applicableItemsBySpec });
	const appliesTo = (
		row: InspectionWithValues | AverageRow | undefined,
		code = WEIGHT_ITEM_CODE,
	) => {
		if (!isInspection(row)) return undefined;
		const specApplies = applicableItemsBySpec?.[row.ProfileSpecId]?.some(
			(item) => item.Code === code,
		);
		return code === WEIGHT_ITEM_CODE
			? weightApplies(specApplies, row.MeasuredLbsPerFoot)
			: specApplies;
	};
	const measurementCellStyle =
		(
			code = WEIGHT_ITEM_CODE,
		): CellStyleFunc<InspectionWithValues | AverageRow> =>
		({ data }) => {
			if (isAverage(data))
				return {
					fontWeight: 600,
					color: "inherit",
					backgroundColor: "transparent",
				};
			const reading = isInspection(data)
				? code === WEIGHT_ITEM_CODE
					? {
							NumericValue: data.MeasuredLbsPerFoot,
							Ltl: data.MinLbsPerFoot,
							Utl: data.MaxLbsPerFoot,
							Checked: null,
						}
					: Object.values(data.values).find((item) => item.Code === code)
				: undefined;
			// Without a specification nothing can be judged, so dashes stay black.
			const evaluated =
				!isInspection(data) ||
				isInspectionEvaluated(data, applicableItemsBySpec, {
					withLegDelta: true,
				});
			return getSummaryMeasurementStyle({
				reading,
				applies: appliesTo(data, code),
				evaluated,
				errorColor: theme.palette.error.main,
				missingValueColor: evaluated
					? theme.palette.error.main
					: theme.palette.text.primary,
				notApplicableBackground: theme.palette.action.disabledBackground,
			});
		};
	const base: ColDef<InspectionWithValues | AverageRow>[] = [
		{
			headerName: "Date",
			colId: "date",
			width: ROLLING_GRID_COLUMN_WIDTHS.date,
			minWidth: ROLLING_GRID_COLUMN_WIDTHS.date,
			pinned: "left",
			type: "centerAligned",
			valueGetter: (
				p: ValueGetterParams<InspectionWithValues | AverageRow>,
			) => {
				const row = p.data;
				if (isAverage(row)) return row.InspectedAt;
				return row?.InspectedAt
					? dxtToLocalServerTime(row.InspectedAt, "dd MMM HH:mm")
					: EMPTY_CELL_VALUE;
			},
			cellRenderer: (
				p: ICellRendererParams<InspectionWithValues | AverageRow>,
			) => (
				<span
					data-test-id={
						isInspection(p.data)
							? `inspection-sample-${p.data.ProfileInspectionId}`
							: undefined
					}
				>
					{p.value}
				</span>
			),
			cellStyle: (p): CellStyle | undefined =>
				isAverage(p.data)
					? { fontWeight: 600, textTransform: "uppercase", fontSize: "11px" }
					: undefined,
		},
		{
			headerName: "Section",
			field: "SectionName",
			width: ROLLING_GRID_COLUMN_WIDTHS.section,
			minWidth: ROLLING_GRID_COLUMN_WIDTHS.section,
			pinned: "left",
			type: "centerAligned",
			valueGetter: (p) => displayValue(p.data?.SectionName),
		},
		{
			headerName: "Heat",
			field: "HeatName",
			width: ROLLING_GRID_COLUMN_WIDTHS.heat,
			pinned: "left",
			type: "centerAligned",
			valueGetter: (p) => displayValue(p.data?.HeatName),
		},
		{
			headerName: "Inspector",
			field: "Inspector",
			width: ROLLING_GRID_COLUMN_WIDTHS.inspector,
			pinned: "left",
			type: "centerAligned",
			valueGetter: (p) => displayValue(p.data?.Inspector),
		},
		{
			headerName: "Wt/ft",
			colId: "weight",
			width: ROLLING_GRID_COLUMN_WIDTHS.weight,
			minWidth: ROLLING_GRID_COLUMN_WIDTHS.weight,
			type: "centerAligned",
			valueGetter: (p) =>
				appliesTo(p.data) === false ? "" : fmt(p.data?.MeasuredLbsPerFoot),
			cellRenderer: ({
				value,
			}: ICellRendererParams<InspectionWithValues | AverageRow, string>) => (
				<MeasurementValue>{value}</MeasurementValue>
			),
			cellStyle: measurementCellStyle(),
			cellClassRules: {
				"inspection-out-of-band": (p) =>
					isInspection(p.data) &&
					(p.data.WeightStatus === "Over" || p.data.WeightStatus === "Under"),
			},
		},
	];

	const dims: ColDef<InspectionWithValues | AverageRow>[] = summaryItems
		.filter((item) => item.Code !== WEIGHT_ITEM_CODE)
		.sort(
			(left, right) =>
				left.SortOrder - right.SortOrder || left.Code.localeCompare(right.Code),
		)
		.map(({ Code: code }) => {
			const spec = specItems.find((item) => item.Code === code);
			return {
				headerName: code,
				headerTooltip: spec?.Name ?? code,
				colId: `dim_${code}`,
				width: ROLLING_GRID_COLUMN_WIDTHS.dimension,
				minWidth: ROLLING_GRID_COLUMN_WIDTHS.dimension,
				type: "centerAligned",
				sortable: false,
				valueGetter: (
					p: ValueGetterParams<InspectionWithValues | AverageRow>,
				) => {
					if (appliesTo(p.data, code) === false) return "";
					if (isAverage(p.data)) {
						const dimension = summaryDimensions.find(
							(item) => item.Code === code,
						);
						return fmt(
							dimension
								? p.data.values[dimension.ProfileItemId]?.NumericValue
								: null,
						);
					}
					const v =
						p.data &&
						Object.values(p.data.values).find((item) => item.Code === code);
					return v ? fmt(v.NumericValue) : EMPTY_CELL_VALUE;
				},
				cellRenderer: (
					p: ICellRendererParams<InspectionWithValues | AverageRow>,
				) => (
					<span
						data-test-id={
							isInspection(p.data)
								? `inspection-value-${p.data.ProfileInspectionId}-${code}`
								: undefined
						}
					>
						<MeasurementValue>{p.value}</MeasurementValue>
					</span>
				),
				cellStyle: measurementCellStyle(code),
				cellClassRules: {
					"inspection-out-of-band": (p) => {
						if (!isInspection(p.data)) return false;
						const value = Object.values(p.data.values).find(
							(item) => item.Code === code,
						);
						return value?.Status === "Over" || value?.Status === "Under";
					},
				},
			};
		});

	const actions: ColDef<InspectionWithValues | AverageRow> = {
		headerName: "",
		colId: "actions",
		width: ROLLING_GRID_COLUMN_WIDTHS.actions,
		minWidth: ROLLING_GRID_COLUMN_WIDTHS.actions,
		maxWidth: ROLLING_GRID_COLUMN_WIDTHS.actions,
		sortable: false,
		filter: false,
		suppressHeaderMenuButton: true,
		cellRenderer: InspectionActionsCell,
		cellRendererParams: { onEdit, onDelete },
	};
	const columnDefs: ColDef<InspectionWithValues | AverageRow>[] = [
		...base.filter(
			(column) =>
				column.colId !== "weight" || hasWeightColumn(summaryItems, inspections),
		),
		...dims,
		actions,
	];

	return { columnDefs };
};
